import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { type IPlacementMove, type ISubtaskMove } from '../../helpers/taskPlacement'
import { createAction } from '../types'

export const prefix = 'myTasks'

export const [LIST_MY_TASKS_REQUEST, LIST_MY_TASKS_SUCCESS, LIST_MY_TASKS_FAILURE] =
  createRequestTypes(prefix, 'LIST_MY_TASKS')

export const listMyTasks = createAction((params: object = {}) => ({
  type: [LIST_MY_TASKS_REQUEST, LIST_MY_TASKS_SUCCESS, LIST_MY_TASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.listMyTasks(params),
  params,
}))

// Sidebar preview reads/writes a separate slice so its 4-item, current-user-only
// fetch can't overwrite the full /my-tasks page list (and vice versa).
export const [
  LIST_MY_TASKS_PREVIEW_REQUEST,
  LIST_MY_TASKS_PREVIEW_SUCCESS,
  LIST_MY_TASKS_PREVIEW_FAILURE,
] = createRequestTypes(prefix, 'LIST_MY_TASKS_PREVIEW')

export const listMyTasksPreview = createAction((params: object = {}) => ({
  type: [
    LIST_MY_TASKS_PREVIEW_REQUEST,
    LIST_MY_TASKS_PREVIEW_SUCCESS,
    LIST_MY_TASKS_PREVIEW_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.listMyTasks(params),
  params,
}))

// The client page's Tasks tab reads/writes its own slice for the same reason as
// the sidebar preview above — a client-scoped fetch must not overwrite the full
// /my-tasks page list (and vice versa).
export const [LIST_CLIENT_TASKS_REQUEST, LIST_CLIENT_TASKS_SUCCESS, LIST_CLIENT_TASKS_FAILURE] =
  createRequestTypes(prefix, 'LIST_CLIENT_TASKS')

export const listClientTasks = createAction((params: object = {}) => ({
  type: [LIST_CLIENT_TASKS_REQUEST, LIST_CLIENT_TASKS_SUCCESS, LIST_CLIENT_TASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.listMyTasks(params),
  params,
}))

// Seeds the nav badge. Its own endpoint (not a one-row page of /my-tasks read for
// `counts.active`) so the seeded value and the live TASK_UNREAD_COUNT_CHANGED
// value come from the same server-side query and can't disagree — the badge counts
// unread tasks, which is deliberately NOT the My Tasks page's Active count. Always
// the current user's own count; the endpoint takes no params.
export const [COUNT_UNREAD_TASKS_REQUEST, COUNT_UNREAD_TASKS_SUCCESS, COUNT_UNREAD_TASKS_FAILURE] =
  createRequestTypes(prefix, 'COUNT_UNREAD_TASKS')

export const countUnreadTasks = createAction(() => ({
  type: [COUNT_UNREAD_TASKS_REQUEST, COUNT_UNREAD_TASKS_SUCCESS, COUNT_UNREAD_TASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.unreadCount(),
}))

export const [
  LIST_TASK_CATEGORIES_REQUEST,
  LIST_TASK_CATEGORIES_SUCCESS,
  LIST_TASK_CATEGORIES_FAILURE,
] = createRequestTypes(prefix, 'LIST_TASK_CATEGORIES')

export const listTaskCategories = createAction(() => ({
  type: [LIST_TASK_CATEGORIES_REQUEST, LIST_TASK_CATEGORIES_SUCCESS, LIST_TASK_CATEGORIES_FAILURE],
  promise: (api: IApi) => api.myTasks.listCategories(),
}))

export const [SHOW_TASK_REQUEST, SHOW_TASK_SUCCESS, SHOW_TASK_FAILURE] = createRequestTypes(
  prefix,
  'SHOW_TASK',
)

// `params.taskDetailId` files the task, its comments and its activity under its own id, so
// two open drawers never overwrite each other. Reducer-only: never sent to the API.
export const showTask = createAction((id: string) => ({
  type: [SHOW_TASK_REQUEST, SHOW_TASK_SUCCESS, SHOW_TASK_FAILURE],
  promise: (api: IApi) => api.myTasks.show(id),
  params: { taskDetailId: id },
}))

// Fired whenever the detail panel shows a task, so "unread" means "never opened
// the panel". Idempotent server-side, so it doesn't need to know what it already
// sent. `params.taskId` lets the reducer clear that row's dot optimistically
// rather than waiting for a list refetch.
export const [MARK_TASK_READ_REQUEST, MARK_TASK_READ_SUCCESS, MARK_TASK_READ_FAILURE] =
  createRequestTypes(prefix, 'MARK_TASK_READ')

export const markTaskRead = createAction((taskId: string) => ({
  type: [MARK_TASK_READ_REQUEST, MARK_TASK_READ_SUCCESS, MARK_TASK_READ_FAILURE],
  promise: (api: IApi) => api.myTasks.markRead(taskId),
  params: { taskId },
}))

// "Mark all read" on My Tasks. Takes the list's own view params (filters, Show
// Completed), so the server clears exactly the rows that view shows; the reply
// carries the new badge count.
export const [
  MARK_ALL_TASKS_READ_REQUEST,
  MARK_ALL_TASKS_READ_SUCCESS,
  MARK_ALL_TASKS_READ_FAILURE,
] = createRequestTypes(prefix, 'MARK_ALL_TASKS_READ')

export const markAllTasksRead = createAction((viewParams: object) => ({
  type: [MARK_ALL_TASKS_READ_REQUEST, MARK_ALL_TASKS_READ_SUCCESS, MARK_ALL_TASKS_READ_FAILURE],
  promise: (api: IApi) => api.myTasks.markAllRead(viewParams),
}))

export const [CREATE_TASK_REQUEST, CREATE_TASK_SUCCESS, CREATE_TASK_FAILURE] = createRequestTypes(
  prefix,
  'CREATE_TASK',
)

export const createTask = createAction((data: object) => ({
  type: [CREATE_TASK_REQUEST, CREATE_TASK_SUCCESS, CREATE_TASK_FAILURE],
  promise: (api: IApi) => api.myTasks.create(data),
}))

export const [UPDATE_TASK_REQUEST, UPDATE_TASK_SUCCESS, UPDATE_TASK_FAILURE] = createRequestTypes(
  prefix,
  'UPDATE_TASK',
)

export const updateTask = createAction((id: string, data: object) => ({
  type: [UPDATE_TASK_REQUEST, UPDATE_TASK_SUCCESS, UPDATE_TASK_FAILURE],
  promise: (api: IApi) => api.myTasks.update(id, data),
  // Lets a reducer apply a status change on REQUEST (the project calendar).
  params: { id, data },
}))

export const [BULK_UPDATE_TASKS_REQUEST, BULK_UPDATE_TASKS_SUCCESS, BULK_UPDATE_TASKS_FAILURE] =
  createRequestTypes(prefix, 'BULK_UPDATE_TASKS')

// Reuses the single update endpoint with the selected ids in the body (a
// representative id goes in the URL), matching the AR/Inventory inline-bulk
// convention. Distinct action types keep it off the single-update panel-sync.
export const bulkUpdateTasks = createAction((data: { ids: string[] } & object) => ({
  type: [BULK_UPDATE_TASKS_REQUEST, BULK_UPDATE_TASKS_SUCCESS, BULK_UPDATE_TASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.update(data.ids[0], data),
}))

export const [LIST_TASK_ACTIVITY_REQUEST, LIST_TASK_ACTIVITY_SUCCESS, LIST_TASK_ACTIVITY_FAILURE] =
  createRequestTypes(prefix, 'LIST_TASK_ACTIVITY')

export const listTaskActivity = createAction((taskId: string, params?: object) => ({
  type: [LIST_TASK_ACTIVITY_REQUEST, LIST_TASK_ACTIVITY_SUCCESS, LIST_TASK_ACTIVITY_FAILURE],
  promise: (api: IApi) => api.myTasks.listActivity(taskId, params),
  params: { ...params, taskDetailId: taskId },
}))

export const [LIST_SUBTASKS_REQUEST, LIST_SUBTASKS_SUCCESS, LIST_SUBTASKS_FAILURE] =
  createRequestTypes(prefix, 'LIST_SUBTASKS')

// `params.parentTaskId` lets the reducer fold the fetched rows into the right
// parent; `params.loadMore` appends for pagination.
export const listSubtasks = createAction((taskId: string, params?: object) => ({
  type: [LIST_SUBTASKS_REQUEST, LIST_SUBTASKS_SUCCESS, LIST_SUBTASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.listSubtasks(taskId, params),
  params,
}))

export const [MOVE_SUBTASK_REQUEST, MOVE_SUBTASK_SUCCESS, MOVE_SUBTASK_FAILURE] =
  createRequestTypes(prefix, 'MOVE_SUBTASK')

// Reorder a subtask among its siblings, move it under a different parent
// (`parentTaskId`), or promote it to a top-level task (`parentTaskId: null`, plus
// the `projectId`/`sectionId` it should land in on a board).
export const moveSubtask = createAction(
  (
    taskId: string,
    data: {
      parentTaskId?: string | null
      position?: number
      projectId?: string
      sectionId?: string
    },
    // Client-only: the moves to show when this request starts (default: just this one), so a
    // batch lands at once, and My Tasks can say which personal section a promoted task lands in.
    preview?: ISubtaskMove[],
  ) => ({
    type: [MOVE_SUBTASK_REQUEST, MOVE_SUBTASK_SUCCESS, MOVE_SUBTASK_FAILURE],
    params: { taskId, ...data, preview },
    promise: (api: IApi) => api.myTasks.moveSubtask(taskId, data),
  }),
)

export const [LIST_TASK_COMMENTS_REQUEST, LIST_TASK_COMMENTS_SUCCESS, LIST_TASK_COMMENTS_FAILURE] =
  createRequestTypes(prefix, 'LIST_TASK_COMMENTS')

export const listTaskComments = createAction((taskId: string, params?: object) => ({
  type: [LIST_TASK_COMMENTS_REQUEST, LIST_TASK_COMMENTS_SUCCESS, LIST_TASK_COMMENTS_FAILURE],
  promise: (api: IApi) => api.myTasks.listComments(taskId, params),
  params: { ...params, taskDetailId: taskId },
}))

export const [
  CREATE_TASK_COMMENT_REQUEST,
  CREATE_TASK_COMMENT_SUCCESS,
  CREATE_TASK_COMMENT_FAILURE,
] = createRequestTypes(prefix, 'CREATE_TASK_COMMENT')

export const createTaskComment = createAction((taskId: string, data: object) => ({
  type: [CREATE_TASK_COMMENT_REQUEST, CREATE_TASK_COMMENT_SUCCESS, CREATE_TASK_COMMENT_FAILURE],
  promise: (api: IApi) => api.myTasks.createComment(taskId, data),
  params: { taskDetailId: taskId },
}))

export const [
  DELETE_TASK_COMMENT_REQUEST,
  DELETE_TASK_COMMENT_SUCCESS,
  DELETE_TASK_COMMENT_FAILURE,
] = createRequestTypes(prefix, 'DELETE_TASK_COMMENT')

export const deleteTaskComment = createAction((taskId: string, commentId: string) => ({
  type: [DELETE_TASK_COMMENT_REQUEST, DELETE_TASK_COMMENT_SUCCESS, DELETE_TASK_COMMENT_FAILURE],
  promise: (api: IApi) => api.myTasks.deleteComment(taskId, commentId),
}))

export const [
  UPDATE_TASK_COMMENT_REQUEST,
  UPDATE_TASK_COMMENT_SUCCESS,
  UPDATE_TASK_COMMENT_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_TASK_COMMENT')

export const updateTaskComment = createAction(
  (taskId: string, commentId: string, data: object) => ({
    type: [UPDATE_TASK_COMMENT_REQUEST, UPDATE_TASK_COMMENT_SUCCESS, UPDATE_TASK_COMMENT_FAILURE],
    promise: (api: IApi) => api.myTasks.updateComment(taskId, commentId, data),
  }),
)

export const [
  UPLOAD_TASK_INLINE_FILE_REQUEST,
  UPLOAD_TASK_INLINE_FILE_SUCCESS,
  UPLOAD_TASK_INLINE_FILE_FAILURE,
] = createRequestTypes(prefix, 'UPLOAD_TASK_INLINE_FILE')

export const uploadTaskInlineFile = createAction((taskId: string, file: File) => ({
  type: [
    UPLOAD_TASK_INLINE_FILE_REQUEST,
    UPLOAD_TASK_INLINE_FILE_SUCCESS,
    UPLOAD_TASK_INLINE_FILE_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.uploadInlineFile(taskId, file),
}))

export const [
  UPLOAD_TASK_COMMENT_ATTACHMENT_REQUEST,
  UPLOAD_TASK_COMMENT_ATTACHMENT_SUCCESS,
  UPLOAD_TASK_COMMENT_ATTACHMENT_FAILURE,
] = createRequestTypes(prefix, 'UPLOAD_TASK_COMMENT_ATTACHMENT')

export const uploadTaskCommentAttachment = createAction((taskId: string, file: File) => ({
  type: [
    UPLOAD_TASK_COMMENT_ATTACHMENT_REQUEST,
    UPLOAD_TASK_COMMENT_ATTACHMENT_SUCCESS,
    UPLOAD_TASK_COMMENT_ATTACHMENT_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.uploadCommentAttachment(taskId, file),
}))

export const [
  ADD_TASK_COMMENT_REACTION_REQUEST,
  ADD_TASK_COMMENT_REACTION_SUCCESS,
  ADD_TASK_COMMENT_REACTION_FAILURE,
] = createRequestTypes(prefix, 'ADD_TASK_COMMENT_REACTION')

export const addTaskCommentReaction = createAction(
  (taskId: string, commentId: string, emoji: string) => ({
    type: [
      ADD_TASK_COMMENT_REACTION_REQUEST,
      ADD_TASK_COMMENT_REACTION_SUCCESS,
      ADD_TASK_COMMENT_REACTION_FAILURE,
    ],
    promise: (api: IApi) => api.myTasks.addCommentReaction(taskId, commentId, emoji),
  }),
)

export const [
  REMOVE_TASK_COMMENT_REACTION_REQUEST,
  REMOVE_TASK_COMMENT_REACTION_SUCCESS,
  REMOVE_TASK_COMMENT_REACTION_FAILURE,
] = createRequestTypes(prefix, 'REMOVE_TASK_COMMENT_REACTION')

export const removeTaskCommentReaction = createAction(
  (taskId: string, commentId: string, emoji: string) => ({
    type: [
      REMOVE_TASK_COMMENT_REACTION_REQUEST,
      REMOVE_TASK_COMMENT_REACTION_SUCCESS,
      REMOVE_TASK_COMMENT_REACTION_FAILURE,
    ],
    promise: (api: IApi) => api.myTasks.removeCommentReaction(taskId, commentId, emoji),
  }),
)

export const [
  GET_TASK_COMMENT_ATTACHMENT_LINK_REQUEST,
  GET_TASK_COMMENT_ATTACHMENT_LINK_SUCCESS,
  GET_TASK_COMMENT_ATTACHMENT_LINK_FAILURE,
] = createRequestTypes(prefix, 'GET_TASK_COMMENT_ATTACHMENT_LINK')

export const getTaskCommentAttachmentLink = createAction((attachmentId: string) => ({
  type: [
    GET_TASK_COMMENT_ATTACHMENT_LINK_REQUEST,
    GET_TASK_COMMENT_ATTACHMENT_LINK_SUCCESS,
    GET_TASK_COMMENT_ATTACHMENT_LINK_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.getCommentAttachmentLink(attachmentId),
}))

export const [
  ADD_TASK_COLLABORATOR_REQUEST,
  ADD_TASK_COLLABORATOR_SUCCESS,
  ADD_TASK_COLLABORATOR_FAILURE,
] = createRequestTypes(prefix, 'ADD_TASK_COLLABORATOR')

export const addTaskCollaborator = createAction((taskId: string, userId: string) => ({
  type: [
    ADD_TASK_COLLABORATOR_REQUEST,
    ADD_TASK_COLLABORATOR_SUCCESS,
    ADD_TASK_COLLABORATOR_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.addCollaborator(taskId, userId),
}))

export const [
  REMOVE_TASK_COLLABORATOR_REQUEST,
  REMOVE_TASK_COLLABORATOR_SUCCESS,
  REMOVE_TASK_COLLABORATOR_FAILURE,
] = createRequestTypes(prefix, 'REMOVE_TASK_COLLABORATOR')

export const removeTaskCollaborator = createAction((taskId: string, userId: string) => ({
  type: [
    REMOVE_TASK_COLLABORATOR_REQUEST,
    REMOVE_TASK_COLLABORATOR_SUCCESS,
    REMOVE_TASK_COLLABORATOR_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.removeCollaborator(taskId, userId),
}))

export const [DELETE_TASK_REQUEST, DELETE_TASK_SUCCESS, DELETE_TASK_FAILURE] = createRequestTypes(
  prefix,
  'DELETE_TASK',
)

export const [
  CANCEL_TASK_ARC_RUN_REQUEST,
  CANCEL_TASK_ARC_RUN_SUCCESS,
  CANCEL_TASK_ARC_RUN_FAILURE,
] = createRequestTypes(prefix, 'CANCEL_TASK_ARC_RUN')

/** Stop Arc mid-run on a task. Deliberately does NOT clear `workingUserId`
 *  optimistically — the ATLAS_AI_TASK_WORKING websocket event clears it for
 *  everyone watching, so the indicator has one code path rather than two that
 *  can disagree. */
export const cancelTaskArcRun = createAction((taskId: string) => ({
  type: [CANCEL_TASK_ARC_RUN_REQUEST, CANCEL_TASK_ARC_RUN_SUCCESS, CANCEL_TASK_ARC_RUN_FAILURE],
  promise: (api: IApi) => api.myTasks.cancelArcRun(taskId),
}))

export const deleteTask = createAction((id: string) => ({
  type: [DELETE_TASK_REQUEST, DELETE_TASK_SUCCESS, DELETE_TASK_FAILURE],
  promise: (api: IApi) => api.myTasks.delete(id),
}))

export const [BULK_DELETE_TASKS_REQUEST, BULK_DELETE_TASKS_SUCCESS, BULK_DELETE_TASKS_FAILURE] =
  createRequestTypes(prefix, 'BULK_DELETE_TASKS')

export const bulkDeleteTasks = createAction((ids: string[]) => ({
  type: [BULK_DELETE_TASKS_REQUEST, BULK_DELETE_TASKS_SUCCESS, BULK_DELETE_TASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.bulkDelete(ids),
}))

export const [MERGE_TASKS_REQUEST, MERGE_TASKS_SUCCESS, MERGE_TASKS_FAILURE] = createRequestTypes(
  prefix,
  'MERGE_TASKS',
)

export const mergeTasks = createAction((id: string, duplicateIds: string[]) => ({
  type: [MERGE_TASKS_REQUEST, MERGE_TASKS_SUCCESS, MERGE_TASKS_FAILURE],
  promise: (api: IApi) => api.myTasks.merge(id, duplicateIds),
}))

export const SEARCH_TASKS = createRequestTypes(prefix, 'SEARCH_TASKS')

// Task picker lookups (merge into another task): top-level tasks by title. Read straight off the result, no reducer.
export const searchTasks = createAction((query: string) => ({
  type: SEARCH_TASKS,
  promise: (api: IApi) =>
    api.myTasks.search({
      query,
      limit: 20,
      includeDescription: false,
      titleOnly: true,
      topLevelOnly: true,
    }),
}))

// ── Personal My Tasks sections ───────────────────────────────────────────────
// None of these touch the list slice: the page refetches after each one, the
// same way the project board does. Sections and placements are cheap to re-read
// and the refetch is what keeps per-section counts honest.

export const [
  CREATE_MY_TASKS_SECTION_REQUEST,
  CREATE_MY_TASKS_SECTION_SUCCESS,
  CREATE_MY_TASKS_SECTION_FAILURE,
] = createRequestTypes(prefix, 'CREATE_MY_TASKS_SECTION')

export const createMyTasksSection = createAction((data: object) => ({
  type: [
    CREATE_MY_TASKS_SECTION_REQUEST,
    CREATE_MY_TASKS_SECTION_SUCCESS,
    CREATE_MY_TASKS_SECTION_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.createMyTasksSection(data),
}))

export const [
  UPDATE_MY_TASKS_SECTION_REQUEST,
  UPDATE_MY_TASKS_SECTION_SUCCESS,
  UPDATE_MY_TASKS_SECTION_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_MY_TASKS_SECTION')

export const updateMyTasksSection = createAction((sectionId: string, data: object) => ({
  type: [
    UPDATE_MY_TASKS_SECTION_REQUEST,
    UPDATE_MY_TASKS_SECTION_SUCCESS,
    UPDATE_MY_TASKS_SECTION_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.updateMyTasksSection(sectionId, data),
}))

export const [
  DELETE_MY_TASKS_SECTION_REQUEST,
  DELETE_MY_TASKS_SECTION_SUCCESS,
  DELETE_MY_TASKS_SECTION_FAILURE,
] = createRequestTypes(prefix, 'DELETE_MY_TASKS_SECTION')

export const deleteMyTasksSection = createAction((sectionId: string) => ({
  type: [
    DELETE_MY_TASKS_SECTION_REQUEST,
    DELETE_MY_TASKS_SECTION_SUCCESS,
    DELETE_MY_TASKS_SECTION_FAILURE,
  ],
  promise: (api: IApi) => api.myTasks.deleteMyTasksSection(sectionId),
}))

export const [
  REORDER_MY_TASKS_SECTIONS_REQUEST,
  REORDER_MY_TASKS_SECTIONS_SUCCESS,
  REORDER_MY_TASKS_SECTIONS_FAILURE,
] = createRequestTypes(prefix, 'REORDER_MY_TASKS_SECTIONS')

export const reorderMyTasksSections = createAction((sectionIds: string[]) => ({
  type: [
    REORDER_MY_TASKS_SECTIONS_REQUEST,
    REORDER_MY_TASKS_SECTIONS_SUCCESS,
    REORDER_MY_TASKS_SECTIONS_FAILURE,
  ],
  // Carried on the REQUEST so the reducer can reorder before the write lands.
  params: { sectionIds },
  promise: (api: IApi) => api.myTasks.reorderMyTasksSections(sectionIds),
}))

export const [
  MOVE_MY_TASKS_PLACEMENT_REQUEST,
  MOVE_MY_TASKS_PLACEMENT_SUCCESS,
  MOVE_MY_TASKS_PLACEMENT_FAILURE,
] = createRequestTypes(prefix, 'MOVE_MY_TASKS_PLACEMENT')

export const moveMyTasksPlacement = createAction(
  (
    data: { taskId: string; sectionId: string | null; position?: number },
    // Client-only: the moves to show when this request starts (default: just this one), so a
    // batch sent one write at a time lands on screen all at once.
    preview?: IPlacementMove[],
  ) => ({
    type: [
      MOVE_MY_TASKS_PLACEMENT_REQUEST,
      MOVE_MY_TASKS_PLACEMENT_SUCCESS,
      MOVE_MY_TASKS_PLACEMENT_FAILURE,
    ],
    // Carried on the REQUEST so the reducer can move the row before the write lands.
    params: { ...data, preview },
    promise: (api: IApi) => api.myTasks.moveMyTasksPlacement(data),
  }),
)

export const SUBSCRIBE_MY_TASKS_UPDATES = `${prefix}/SUBSCRIBE_MY_TASKS_UPDATES`
export const UNSUBSCRIBE_MY_TASKS_UPDATES = `${prefix}/UNSUBSCRIBE_MY_TASKS_UPDATES`
export const MY_TASKS_UPDATES_RESYNC = `${prefix}/MY_TASKS_UPDATES_RESYNC`

export const subscribeToMyTasksUpdates = () => ({ type: SUBSCRIBE_MY_TASKS_UPDATES })
export const unsubscribeFromMyTasksUpdates = () => ({ type: UNSUBSCRIBE_MY_TASKS_UPDATES })
export const resyncMyTasksUpdates = () => ({ type: MY_TASKS_UPDATES_RESYNC })
